import axios from 'axios';

const api = axios.create({
    baseURL: '/api',
    headers: {
        'Content-Type': 'application/json',
    },
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    const lang = localStorage.getItem('maison_lang');
    if (lang) {
        config.headers['X-App-Lang'] = lang;
    }
    return config;
});

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth
export const login = (data: any) => api.post('/auth/login', data);
export const getMe = () => api.get('/users/me');
export const getUsers = (params?: { skip?: number; limit?: number }) => api.get('/users/', { params });
export const completeInitialCredentials = (payload: {
    new_email?: string;
    confirm_email?: string;
    new_password?: string;
    confirm_password?: string;
}) => api.post('/users/me/initial-credentials', payload);
export const checkIntro = (intro_text: string) => api.post('/users/me/intro/check', { intro_text });
export const updateIntro = (intro_text: string) => api.post('/users/me/intro', { intro_text });
export const getUserProfile = (userId: number) => api.get(`/users/${userId}`);
export const getUserPosts = (userId: number) => api.get(`/users/${userId}/posts`);

// Posts
export const getPosts = (feed?: string) => {
    const params = feed ? { feed } : undefined;
    return api.get('/posts/', { params });
};
export const createPost = (data: any) => api.post('/posts/', data);
export const checkSmile = (content: string) => api.post('/posts/check', { content });
export const checkMedia = (
    media_url: string,
    media_type: 'image' | 'video' | 'audio',
    content?: string
) => api.post('/posts/media-check', { media_url, media_type, content });
export const deletePost = (postId: number) => api.delete(`/posts/${postId}`);
export const likePost = (postId: number, emoji?: string) =>
    api.post(`/posts/${postId}/like`, { emoji });
export const getPostLikes = (postId: number) => api.get(`/posts/${postId}/likes`);
export const checkComment = (postId: number, content: string) =>
    api.post(`/posts/${postId}/comments/check`, { content });
export const getPostComments = (postId: number) => api.get(`/posts/${postId}/comments`);
export const createComment = (postId: number, content: string) =>
    api.post(`/posts/${postId}/comments`, { content });
export const undoComment = (postId: number, commentId: number) =>
    api.delete(`/posts/${postId}/comments/${commentId}`);
export const getPostCommentStatus = (postIds: number[]) =>
    api.post('/posts/comment-status', { post_ids: postIds });
export const getSpotlightStatus = () => api.get('/posts/spotlight');
export const toggleSpotlight = (postId: number) => api.post(`/posts/${postId}/spotlight`);
export const toggleSelfSpotlight = (postId: number) => api.post(`/posts/${postId}/self-spotlight`);
export const checkPoll = (question: string, options: string[]) =>
    api.post('/posts/polls/check', { question, options });
export const createPoll = (data: any) => api.post('/posts/polls', data);
export const getPoll = (pollId: number) => api.get(`/posts/polls/${pollId}`);
export const votePoll = (pollId: number, option_id: number) =>
    api.post(`/posts/polls/${pollId}/vote`, { option_id });

// Videos
export const getVideos = () => api.get('/videos/');

// Groups
export const getGroups = () => api.get('/groups/');
export const getGroup = (groupId: number) => api.get(`/groups/${groupId}`);
export const createGroup = (data: any) => api.post('/groups/', data);
export const openDirectGroup = (user_id: number) => api.post('/groups/direct', { user_id });
export const updateGroup = (groupId: number, data: any) => api.patch(`/groups/${groupId}`, data);
export const addGroupMembers = (groupId: number, member_ids: number[]) =>
    api.post(`/groups/${groupId}/members`, { member_ids });
export const removeGroupMembers = (groupId: number, member_ids: number[], keep_logs = true) =>
    api.post(`/groups/${groupId}/members/remove`, { member_ids, keep_logs });
export const getGroupMembers = (groupId: number) => api.get(`/groups/${groupId}/members`);
export const requestGroupDelete = (groupId: number) => api.post(`/groups/${groupId}/delete`);
export const getGroupMessages = (
    groupId: number,
    params?: { skip?: number; limit?: number; anchor_message_id?: number; before_message_id?: number }
) => api.get(`/chat/${groupId}/messages`, { params });
export const createGroupMessage = (groupId: number, data: any) =>
    api.post(`/chat/${groupId}/messages`, data);
export const reactToChatMessage = (groupId: number, messageId: number, emoji: string) =>
    api.post(`/chat/${groupId}/messages/${messageId}/reaction`, { emoji });
export const cancelChatMessage = (groupId: number, messageId: number) =>
    api.post(`/chat/${groupId}/messages/${messageId}/cancel`);
export const uploadChatAttachment = (
    file: File,
    groupId: number,
    onProgress?: (percent: number) => void
) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('group_id', String(groupId));
    return api.post('/chat/upload', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (event) => {
            if (event.total) {
                const percent = Math.round((event.loaded / event.total) * 100);
                onProgress?.(percent);
            }
        },
    });
};
export const getChatNotificationSummary = () => api.get('/chat/notifications/summary');
export const markChatNotificationsRead = (payload: { notification_ids?: number[]; group_id?: number; event_type?: string }) =>
    api.post('/chat/notifications/read', payload);
export const getChatGroupStats = () => api.get('/chat/group-stats');
export const markChatGroupRead = (groupId: number, message_id?: number) =>
    api.post(`/chat/${groupId}/read`, { message_id });
export const getChatGroupOrder = () => api.get('/chat/group-order');
export const setChatGroupOrder = (group_ids: number[]) =>
    api.post('/chat/group-order', { group_ids });
export const getChatGroupPins = () => api.get('/chat/group-pins');
export const setChatGroupPin = (groupId: number, pinned: boolean) =>
    api.post(`/chat/group-pins/${groupId}`, { pinned });
export const searchChatMessages = (q: string) =>
    api.get('/chat/search', { params: { q } });
export const getChatMuteSettings = (groupId: number) =>
    api.get(`/chat/${groupId}/mute`);
export const updateChatMuteSettings = (groupId: number, payload: { mute_all: boolean; mute_night: boolean; muted_user_ids: number[] }) =>
    api.post(`/chat/${groupId}/mute`, payload);
export const getFriends = () => api.get('/friends/');
export const getFriendRequests = () => api.get('/friends/requests');

// Translate
export const translateText = (text: string, target_lang: string, source_lang?: string) =>
    api.post('/translate', { text, target_lang, source_lang });

// Toku
export const getTokuLeaderboard = (period: 'daily' | 'weekly' | 'monthly' | 'total') =>
    api.get('/toku/leaderboard', { params: { period } });

// U-Store
export const getUStoreItems = () => api.get('/ustore/items');
export const createUStoreItem = (payload: {
    title: string;
    description: string;
    points_cost: number;
    image_url?: string | null;
}) => api.post('/ustore/items', payload);
export const requestUStorePurchase = (itemId: number) =>
    api.post(`/ustore/items/${itemId}/request`);
export const approveUStorePurchase = (contactId: number) =>
    api.post(`/ustore/requests/${contactId}/approve`);
export const rejectUStorePurchase = (contactId: number) =>
    api.post(`/ustore/requests/${contactId}/reject`);
export const uploadUStoreImage = (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/ustore/upload', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};

// Theater
export const getTheaterItems = (
    groupId: number,
    params?: {
        type?: string;
        title?: string;
        from_date?: string;
        to_date?: string;
        folder_id?: number;
    }
) => api.get(`/theater/${groupId}`, { params });
export const getTheaterFolders = (groupId: number) =>
    api.get(`/theater/${groupId}/folders`);
export const createTheaterFolder = (
    groupId: number,
    payload: { name: string; parent_id?: number | null }
) => api.post(`/theater/${groupId}/folders`, payload);
export const moveTheaterItem = (itemId: number, folder_id: number | null) =>
    api.post(`/theater/items/${itemId}/move`, { folder_id });
export const deleteTheaterItem = (itemId: number) =>
    api.delete(`/theater/items/${itemId}`);
export const createTheaterItem = (
    groupId: number,
    payload: {
        item_type: string;
        title?: string;
        folder_id?: number | null;
        content_text?: string;
        media_url?: string;
        thumb_url?: string | null;
        hls_url?: string | null;
        fallback_url?: string | null;
    }
) =>
    api.post(`/theater/${groupId}`, payload);
export const createTheaterNote = (groupId: number, message_id: number) =>
    api.post(`/theater/${groupId}/note`, { message_id });
export const uploadTheaterMedia = (file: File, onProgress?: (percent: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/theater/upload', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (event) => {
            if (event.total) {
                const percent = Math.round((event.loaded / event.total) * 100);
                onProgress?.(percent);
            }
        },
    });
};

// Admin
export const getAdminStats = () => api.get('/admin/stats');
export const getAIAdminOverview = () => api.get('/admin/ai/overview');
export const getAuditLogs = (params?: {
    limit?: number;
    offset?: number;
    actor_id?: number;
    action?: string;
    target_type?: string;
    target_id?: number;
}) => api.get('/admin/audit', { params });

// Contact
export const createContact = (
    category: string,
    message: string,
    recipient_id?: number,
    inquiry_number?: string,
    attachment_urls?: string[]
) => api.post('/contact', { category, message, recipient_id, inquiry_number, attachment_urls });

export const createPublicContact = (
    guest_name: string,
    guest_email: string,
    category: string,
    message: string,
    inquiry_number?: string,
    attachment_urls?: string[]
) => api.post('/contact/public', { guest_name, guest_email, category, message, inquiry_number, attachment_urls });
export const getMyContacts = (type: 'all' | 'inquiry' | 'audience' = 'all') =>
    api.get(`/contact/mine?type=${type}`);
export const markAudienceRead = (contactId: number) =>
    api.post(`/contact/audience/${contactId}/read`);
export const getStaffUsers = () => api.get('/users/staff');
export const getDailyStatus = () => api.get('/users/me/daily-status');
export const getAdminInquiries = () => api.get('/admin/inquiries');
export const getAdminAudience = () => api.get('/admin/audience');
export const getAdminInquiriesByArchive = (archived = false) =>
    api.get('/admin/inquiries', { params: { archived } });
export const getAdminSentInquiriesByArchive = (archived = false) =>
    api.get('/admin/inquiries/sent', { params: { archived } });
export const archiveInquiry = (contactId: number) =>
    api.post(`/admin/inquiries/${contactId}/archive`);
export const unarchiveInquiry = (contactId: number) =>
    api.post(`/admin/inquiries/${contactId}/unarchive`);

export const uploadContactAttachment = (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/contact/upload', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};

// Auth (Extended)
export const registerUser = (data: any) => api.post('/auth/register', data);
export const verifyEmail = (token: string, email: string, agreed_to_terms: boolean) => api.post('/auth/verify-email', { token, email, agreed_to_terms });

// Upload
export const uploadFile = (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};

export default api;
